import type {
  tCloseSource,
  tCloseType,
} from 'src/components/LeadClose/LeadClose.types';

/**
 * LEA-50: analytics for LeadClose (Vendido / Perdido / Resuelto), on the front.
 *
 * The saved result is the server's fact (`lead.close.registered`, emitted by
 * the backend); these four are the intent and the abandonment only the screen
 * sees. They live under their own `front.lead_close.` family on purpose: the
 * `front.funnels.` and `front.chat.` prefixes feed the web-activity metric
 * union, and a drag that ends nowhere or a form closed unsaved is not
 * activity. Names follow `front.<domain>.<entity>.<action>`.
 */
export const LEAD_CLOSE_EVENTS = {
  /**
   * One per board drag of a lead card that showed the close tray, at the drop.
   * Carries how it ended (`result`), so the share of drags that end on a box
   * reads against every drag that could have.
   */
  DRAG_ENDED: 'front.lead_close.drag.ended',

  /** The chat bar's "Marcar resultado" menu went from closed to open. */
  MENU_OPENED: 'front.lead_close.menu.opened',

  /**
   * A result form opened to register a result, from any entry point. Carries
   * `attempt_id`, the Idempotency-Key its first save sends, which the
   * backend's `lead.close.registered` carries too.
   */
  FORM_OPENED: 'front.lead_close.form.opened',

  /** A result form closed without a successful save. */
  FORM_ABANDONED: 'front.lead_close.form.abandoned',
} as const;

/** Union of every valid LeadClose analytics event name. */
export type tLeadCloseEventName =
  (typeof LEAD_CLOSE_EVENTS)[keyof typeof LEAD_CLOSE_EVENTS];

/**
 * How a drag ended, the `result` property of DRAG_ENDED.
 *
 * - `close_box`: dropped on a Vendido / Perdido / Resuelto box (the form opens).
 * - `stage_moved`: dropped on another stage; the move was asked for. Whether
 *   the server took it stays with `front.funnels.lead.stage_moved`.
 * - `same_stage`: dropped back on the stage it left.
 * - `no_destination`: dropped where nothing takes it, or cancelled.
 */
export const LEAD_CLOSE_DRAG_RESULTS = [
  'close_box',
  'stage_moved',
  'same_stage',
  'no_destination',
] as const;

export type tLeadCloseDragResult = (typeof LEAD_CLOSE_DRAG_RESULTS)[number];

/**
 * What closed an unsaved form, the `via` property of FORM_ABANDONED: the
 * dialog's X, Escape, its Cancel button, or anything else (the backdrop, the
 * lead deleted under it, another form opened over it).
 */
export const LEAD_CLOSE_ABANDON_VIA = [
  'close_button',
  'escape',
  'cancel',
  'other',
] as const;

export type tLeadCloseAbandonVia = (typeof LEAD_CLOSE_ABANDON_VIA)[number];

export type tLeadCloseDragEndedProps = {
  result: tLeadCloseDragResult;
  /** Only when `result` is `close_box`. */
  type?: tCloseType;
  lead_id: string;
  funnel_id: string | null;
  from_stage_id: string;
  /** Only when `result` is `stage_moved`. */
  to_stage_id?: string;
};

export type tLeadCloseMenuOpenedProps = {
  lead_id: string;
};

export type tLeadCloseFormOpenedProps = {
  type: tCloseType;
  source: tCloseSource;
  lead_id: string;
  attempt_id: string;
};

export type tLeadCloseFormAbandonedProps = tLeadCloseFormOpenedProps & {
  via: tLeadCloseAbandonVia;
  /** Whole seconds between the opening and the close. */
  seconds_open: number;
  /** A save of this form failed before it closed. */
  after_error: boolean;
};
